import { useEffect, useState } from "react";
import api, { fmtDateTime } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import MediaUploader from "../components/common/MediaUploader";
import { Thumb } from "../components/common/MediaUploader";
import { Save, Building2, Palette, MessageSquare, CheckCircle2, Database, Download, Trash2, PlayCircle, RefreshCw } from "lucide-react";

export default function SettingsPage() {
  const [form, setForm] = useState(null);
  const [sms, setSms] = useState([]);
  const [backups, setBackups] = useState([]);
  const [backupBusy, setBackupBusy] = useState(false);

  const loadBackups = () => api.get("/backups").then((r) => setBackups(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data));
    api.get("/sms-queue").then((r) => setSms(r.data));
    loadBackups();
  }, []);

  const save = async () => {
    try {
      const { data } = await api.put("/settings", form);
      setForm(data);
      toast.success("Kaydedildi. Yazdırma önizlemesi artık firma bilgilerinizi kullanır.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Kayıt başarısız"); }
  };

  const markSent = async (id) => { await api.post(`/sms-queue/${id}/mark-sent`); toast.success("Gönderildi olarak işaretlendi"); api.get("/sms-queue").then((r) => setSms(r.data)); };

  const triggerBackup = async () => {
    setBackupBusy(true);
    try {
      await api.post("/backups");
      toast.success("Yedek alındı");
      loadBackups();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yedek alınamadı");
    } finally {
      setBackupBusy(false);
    }
  };

  const downloadBackup = async (b) => {
    try {
      const r = await api.get(`/backups/${b.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = b.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("İndirme başarısız");
    }
  };

  const deleteBackup = async (b) => {
    if (!window.confirm(`${b.filename} silinsin mi?`)) return;
    try {
      await api.delete(`/backups/${b.id}`);
      toast.success("Silindi");
      loadBackups();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  const fmtSize = (n) => {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  };

  const KIND_BADGE = { manual: "bg-sky-600", daily: "bg-emerald-600", weekly: "bg-indigo-600" };

  if (!form) return <div className="text-slate-500">Yükleniyor…</div>;

  return (
    <div className="space-y-5" data-testid="settings-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1B4D3E]">Yönetim</div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">Firma Ayarları & Tema</h1>
      </div>

      <Tabs defaultValue="firm">
        <TabsList>
          <TabsTrigger value="firm" data-testid="tab-firm"><Building2 size={14} className="mr-1" /> Firma Bilgileri</TabsTrigger>
          <TabsTrigger value="theme" data-testid="tab-theme"><Palette size={14} className="mr-1" /> PDF Tema</TabsTrigger>
          <TabsTrigger value="sms" data-testid="tab-sms"><MessageSquare size={14} className="mr-1" /> SMS Kuyruğu</TabsTrigger>
          <TabsTrigger value="backup" data-testid="tab-backup"><Database size={14} className="mr-1" /> Yedekleme</TabsTrigger>
        </TabsList>

        <TabsContent value="firm">
          <Card className="border-slate-200">
            <CardContent className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Firma Adı</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="s-name" /></div>
              <div><Label>Telefon</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="s-phone" /></div>
              <div><Label>E-posta</Label><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="s-email" /></div>
              <div><Label>Vergi Dairesi</Label><Input value={form.tax_office || ""} onChange={(e) => setForm({ ...form, tax_office: e.target.value })} data-testid="s-tax-office" /></div>
              <div><Label>Vergi No</Label><Input value={form.tax_no || ""} onChange={(e) => setForm({ ...form, tax_no: e.target.value })} data-testid="s-tax-no" /></div>
              <div className="sm:col-span-2"><Label>Adres</Label><Textarea value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="s-address" /></div>
              <div className="sm:col-span-2"><Label>Alt Bilgi / Not</Label><Textarea value={form.footer_note || ""} onChange={(e) => setForm({ ...form, footer_note: e.target.value })} placeholder="Örn: Fiyatlarımız 30 gün geçerlidir." data-testid="s-footer" /></div>
              <div className="sm:col-span-2 pt-2 border-t border-slate-200">
                <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2">SMS/WhatsApp Sağlayıcısı</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Sağlayıcı</Label>
                    <Select value={form.sms_provider || "mock"} onValueChange={(v) => setForm({ ...form, sms_provider: v })}>
                      <SelectTrigger data-testid="s-sms-provider"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mock">Mock (Simülasyon)</SelectItem>
                        <SelectItem value="twilio">Twilio</SelectItem>
                        <SelectItem value="netgsm">NetGSM (TR)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Gönderici Numara / Başlık</Label><Input value={form.sms_from || ""} onChange={(e) => setForm({ ...form, sms_from: e.target.value })} placeholder="+90… veya TRAKTORPRO" data-testid="s-sms-from" /></div>
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  Twilio/NetGSM için API anahtarları backend .env üzerinden eklenir. Mock modda mesajlar aşağıdaki SMS Kuyruğu sekmesinde görüntülenir ve "gönderildi" olarak işaretlenir.
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="theme">
          <Card className="border-slate-200">
            <CardContent className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Logo</Label>
                <MediaUploader value={form.logo_media_id ? [form.logo_media_id] : []} onChange={(m) => setForm({ ...form, logo_media_id: m[0] || null })} testid="s-logo" />
              </div>
              <div>
                <Label>Kaşe / İmza</Label>
                <MediaUploader value={form.stamp_media_id ? [form.stamp_media_id] : []} onChange={(m) => setForm({ ...form, stamp_media_id: m[0] || null })} testid="s-stamp" />
              </div>
              <div>
                <Label>Ana Renk</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={form.primary_color || "#1B4D3E"} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="h-10 w-16 rounded cursor-pointer" data-testid="s-primary" />
                  <Input value={form.primary_color || "#1B4D3E"} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="font-mono" />
                </div>
              </div>
              <div>
                <Label>İkincil Renk</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={form.secondary_color || "#D97706"} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })} className="h-10 w-16 rounded cursor-pointer" data-testid="s-secondary" />
                  <Input value={form.secondary_color || "#D97706"} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })} className="font-mono" />
                </div>
              </div>
              <div className="sm:col-span-2 border-t pt-4">
                <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-3">Önizleme (Yazdırılabilir Başlık)</div>
                <div className="border rounded-lg p-6 bg-white">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      {form.logo_media_id ? <div className="h-16 w-16 rounded-lg overflow-hidden border"><Thumb id={form.logo_media_id} /></div> : <div className="h-16 w-16 rounded-lg grid place-items-center text-white font-extrabold text-lg" style={{ background: form.primary_color }}>{(form.name || "TP").slice(0, 2).toUpperCase()}</div>}
                      <div>
                        <div className="text-xl font-extrabold" style={{ color: form.primary_color }}>{form.name || "TraktörPro"}</div>
                        <div className="text-xs text-slate-500">{form.address}</div>
                        <div className="text-xs text-slate-500">{form.phone} · {form.email}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge style={{ background: form.secondary_color, color: "white" }}>TEKLİF / FATURA</Badge>
                      <div className="text-xs text-slate-500 mt-1">V.D. {form.tax_office || "—"} · VKN {form.tax_no || "—"}</div>
                    </div>
                  </div>
                  {form.footer_note && <div className="mt-4 text-xs text-slate-500 border-t pt-2">{form.footer_note}</div>}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sms">
          <Card className="border-slate-200">
            <div className="p-4 border-b border-slate-200 flex items-center gap-2 text-sm text-slate-600">
              <MessageSquare size={16} className="text-[#D97706]" />
              Parça siparişi "Hazır" işaretlendiğinde otomatik SMS kuyruğa eklenir. Mock modda otomatik gönderildi işaretlenir.
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tarih</TableHead>
                    <TableHead>Alıcı</TableHead>
                    <TableHead>Mesaj</TableHead>
                    <TableHead>Durum</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sms.map((m) => (
                    <TableRow key={m.id} data-testid={`sms-row-${m.id}`}>
                      <TableCell className="text-xs">{fmtDateTime(m.created_at)}</TableCell>
                      <TableCell className="font-mono text-xs">{m.to}</TableCell>
                      <TableCell className="max-w-md truncate text-sm">{m.message}</TableCell>
                      <TableCell><Badge className={m.status === "sent" ? "bg-emerald-600 text-white" : "bg-amber-500 text-slate-900"}>{m.status === "sent" ? "Gönderildi" : "Bekliyor"}</Badge></TableCell>
                      <TableCell>{m.status !== "sent" && <Button size="sm" variant="outline" className="gap-1" onClick={() => markSent(m.id)}><CheckCircle2 size={12} /> Gönderildi</Button>}</TableCell>
                    </TableRow>
                  ))}
                  {sms.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500">Henüz mesaj yok.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="backup">
          <Card className="border-slate-200" data-testid="backup-panel">
            <CardContent className="p-4 sm:p-6 space-y-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 text-base font-bold text-slate-900">
                    <Database size={18} className="text-[#1B4D3E]" /> Veritabanı Yedekleri
                  </div>
                  <div className="text-sm text-slate-500 mt-1 max-w-2xl">
                    Her gece 00:00 UTC otomatik yedek alınır (Pazar günleri haftalık olarak etiketlenir). Son <b>7 günlük</b> ve <b>4 haftalık</b> yedek saklanır. Manuel yedekler otomatik silinmez — istediğinizde indirebilir veya kaldırabilirsiniz.
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="gap-2" onClick={loadBackups} data-testid="refresh-backups-btn">
                    <RefreshCw size={14} /> Yenile
                  </Button>
                  <Button className="bg-[#1B4D3E] hover:bg-[#163e32] gap-2" onClick={triggerBackup} disabled={backupBusy} data-testid="trigger-backup-btn">
                    <PlayCircle size={14} /> {backupBusy ? "Alınıyor…" : "Şimdi Yedekle"}
                  </Button>
                </div>
              </div>

              <div className="overflow-x-auto border border-slate-200 rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Dosya</TableHead>
                      <TableHead>Tür</TableHead>
                      <TableHead className="text-right">Boyut</TableHead>
                      <TableHead className="text-right">Kayıt</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {backups.map((b) => (
                      <TableRow key={b.id} data-testid={`backup-row-${b.id}`}>
                        <TableCell className="text-xs whitespace-nowrap">{fmtDateTime(b.created_at)}</TableCell>
                        <TableCell className="font-mono text-xs truncate max-w-[260px]">{b.filename}</TableCell>
                        <TableCell><Badge className={`${KIND_BADGE[b.kind] || "bg-slate-500"} text-white`}>{b.kind?.toUpperCase()}</Badge></TableCell>
                        <TableCell className="text-right text-xs tabular-nums">{fmtSize(b.size)}</TableCell>
                        <TableCell className="text-right text-xs tabular-nums">{b.total_documents?.toLocaleString("tr-TR") || 0}</TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex gap-1">
                            <Button size="sm" variant="outline" className="gap-1" onClick={() => downloadBackup(b)} data-testid={`download-backup-${b.id}`}>
                              <Download size={12} /> İndir
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => deleteBackup(b)} data-testid={`delete-backup-${b.id}`}>
                              <Trash2 size={12} className="text-rose-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {backups.length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Henüz yedek yok. "Şimdi Yedekle" ile ilk yedeği alabilirsiniz.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="text-xs text-slate-500 border-t border-slate-100 pt-3">
                <b>İpucu:</b> İndirilen .zip dosyası her koleksiyonu ayrı JSON dosyası olarak içerir ve <code>manifest.json</code> dosyasında yedek özeti bulunur. Geri yüklemek için şimdilik dosyayı sistem yöneticinize iletin.
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end sticky bottom-4">
        <Button className="bg-[#1B4D3E] hover:bg-[#163e32] gap-2 shadow-lg" onClick={save} data-testid="save-settings-btn"><Save size={16} /> Kaydet</Button>
      </div>
    </div>
  );
}
